import axios from 'axios';
import useMeStore from '@/stores/me';
import apiBase from '@/api/apiBase';

const instance = axios.create({
  baseURL: apiBase,
  timeout: 15 * 1000,
});

// Helpers
instance.interceptors.request.use((config) => {
  if (config.skipAuth) {
    return config;
  }

  const me = useMeStore();
  // eslint-disable-next-line no-param-reassign
  config.headers.Authorization = me.token ? `Bearer ${me.token}` : null;
  return config;
});

// Me
const login = async (payload) => {
  const me = useMeStore();
  const response = await instance.post('/me/login', payload);
  me.login({
    id: response.data.user.id,
    email: response.data.user.email,
    token: response.data.token,
  });
  return response.data;
};

const changePassword = async (payload) => {
  const response = await instance.post('/me/change-password', payload);
  return response.data;
};

const requestPasswordReset = async (email) => {
  const response = await instance.post('/me/password-reset/request', { email }, { skipAuth: true });
  return response.data;
};

const submitPasswordReset = async (payload) => {
  const response = await instance.post('/me/password-reset/submit', payload, { skipAuth: true });
  return response.data;
};

const getDashboardData = async () => {
  const response = await instance.get('/me/dashboard-stats');
  return response.data;
};

const getPermissions = async () => {
  const response = await instance.get('/me/permissions');
  return response.data.permissions;
};

// Visit
const getBranches = async (coordinates) => {
  const response = await instance.get('/visit/branches', {
    params: coordinates,
  });
  return response.data.branches;
};

const getActivityTypes = async () => {
  const response = await instance.get('/visit/activity-types');
  return response.data.activityTypes;
};

const getEmployees = async () => {
  const response = await instance.get('/visit/employees');
  return response.data.employees;
};

const uploadAttachment = async (attachment) => {
  const formData = new FormData();
  formData.append('attachment', attachment);
  const response = await instance.post('/visit/attachment', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 60 * 1000,
  });
  if (response.data.fileName) {
    return response.data.fileName;
  }
  return null;
};

const saveVisit = async (visit) => {
  const response = await instance.post('visit/', visit);
  return response.data.visit.id;
};

const deleteVisit = async (id) => {
  const response = await instance.delete(`visit/${id}`);
  return response.data;
};

// Statistics
const getVisits = async (params) => {
  const response = await instance.get('/statistics/', {
    params,
  });
  return response.data.visits;
};

const getVisit = async (id) => {
  const response = await instance.get(`/visit/${id}`);
  return response.data.visit;
};

const getUsers = async () => {
  const response = await instance.get('/statistics/authors');
  return response.data.authors;
};

// Documents
const getDocuments = async () => {
  const response = await instance.get('/documents/');
  const { documents } = response.data;
  documents.sort((a, b) => a.order - b.order);
  return documents;
};

const editDocuments = async (payload) => {
  const response = await instance.put('/documents/', payload);
  const { documents } = response.data;
  documents.sort((a, b) => a.order - b.order);
  return documents;
};

const uploadDocument = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await instance.post('/documents/file', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 60 * 1000,
  });
  if (response.data.fileUpload) {
    return response.data.fileUpload;
  }
  return null;
};

export {
  login,
  changePassword,
  requestPasswordReset,
  submitPasswordReset,
  getDashboardData,
  getPermissions,

  getBranches,
  getActivityTypes,
  getEmployees,
  uploadAttachment,
  saveVisit,
  deleteVisit,

  getVisits,
  getVisit,
  getUsers,

  getDocuments,
  editDocuments,
  uploadDocument,
};
